import { memo, useMemo } from 'react'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import { useI18n } from '../i18n/I18nContext'

interface ErrorDisplayProps {
  error: string
  onRetry?: () => void
  onReload?: () => void
  showDebugInfo?: boolean
}

type ErrorKind = 'auth' | 'permission' | 'rate-limit' | 'network' | 'timeout' | 'generic'

function classifyError(error: string): ErrorKind {
  const lowerError = error.toLowerCase()
  if (lowerError.includes('401') || lowerError.includes('unauthorized')) return 'auth'
  if (lowerError.includes('403') || lowerError.includes('forbidden')) return 'permission'
  if (lowerError.includes('429') || lowerError.includes('rate limit')) return 'rate-limit'
  if (lowerError.includes('network') || lowerError.includes('fetch')) return 'network'
  if (lowerError.includes('timeout')) return 'timeout'
  return 'generic'
}

export const ErrorDisplay = memo(function ErrorDisplay({
  error,
  onRetry,
  onReload,
  showDebugInfo = false
}: ErrorDisplayProps) {
  const { t } = useI18n()
  const errorType = classifyError(error)

  const errorInfo = useMemo(() => {
    const iconByKind: Record<ErrorKind, string> = {
      auth: '🔐',
      permission: '🚫',
      'rate-limit': '⏱️',
      network: '🌐',
      timeout: '⏰',
      generic: '❌',
    }
    if (errorType === 'generic') {
      return {
        icon: iconByKind.generic,
        title: t('errors.generic.title'),
        description: t('errors.generic.description', { detail: error }),
        solution: t('errors.generic.solution'),
      }
    }
    const base: Record<Exclude<ErrorKind, 'generic'>, string> = {
      auth: 'errors.auth',
      permission: 'errors.permission',
      'rate-limit': 'errors.rateLimit',
      network: 'errors.network',
      timeout: 'errors.timeout',
    }
    const p = base[errorType]
    return {
      icon: iconByKind[errorType],
      title: t(`${p}.title`),
      description: t(`${p}.description`),
      solution: t(`${p}.solution`),
    }
  }, [error, errorType, t])

  return (
    <div className="error-display">
      <div className="error-icon-large">{errorInfo.icon}</div>
      <h3 className="error-title">{errorInfo.title}</h3>
      <p className="error-description">{errorInfo.description}</p>

      <div className="error-solution">
        <div className="solution-icon">💡</div>
        <div className="solution-content">
          <strong>{t('errors.solution.label')}</strong>
          <p>{errorInfo.solution}</p>
        </div>
      </div>

      {showDebugInfo && (
        <div className="debug-info">
          <details>
            <summary>{t('errors.debug.summary')}</summary>
            <div className="debug-content">
              <p><strong>{t('errors.debug.apiUrl')}</strong> {getApiBaseUrl()}</p>
              <p><strong>{t('errors.debug.mode')}</strong> {import.meta.env.MODE}</p>
              <p><strong>{t('errors.debug.fullError')}</strong></p>
              <pre>{error}</pre>
            </div>
          </details>
        </div>
      )}

      <div className="error-actions">
        {onRetry && (
          <button type="button" onClick={onRetry} className="btn-primary">
            {t('errors.retry')}
          </button>
        )}
        {onReload && (
          <button type="button" onClick={onReload} className="btn-secondary">
            {t('errors.reloadPage')}
          </button>
        )}
      </div>
    </div>
  )
})
